import React from "react";

const money = (value) => (value === null || value === undefined || value === "" ? "—" : value);

// Optional charge-type fields (see VendorUsFoodParser/VendorVestaFoodserviceParser
// to_h and Layers::DefaultInvoiceFormat) -- shown only when present so a plain
// invoice with none of these doesn't get a wall of "—" rows.
const CHARGE_FIELDS = [
  ["sales_tax", "Sales Tax"],
  ["surcharge", "Surcharge"],
  ["shipping_fee", "Shipping"],
  ["deposit", "Deposit"],
  ["other_fees", "Other Fees"],
  ["discount", "Discount"],
  ["credit", "Credit"],
];

// Table-shaped view of a flat extraction result -- the shape
// VendorInvoicePdfExtractor/VendorUsFoodParser/VendorVestaFoodserviceParser's
// #to_h produces (vendor_name, invoice_number, ..., line_items: [{item_number,
// description, uom, qty, unit_price, extended_price}]). Used both by
// TestResultView (vendor parser builder's "Test this spec" flow) and Step
// 3.2.1's result, which share this exact shape.
const InvoicePreview = ({ data }) => {
  const lineItems = data.line_items || [];
  const showItemNumber = lineItems.some((item) => item.item_number);
  const showUom = lineItems.some((item) => item.uom);
  const hasAddresses = data.bill_to || data.ship_to;
  const charges = CHARGE_FIELDS.filter(([key]) => data[key] !== null && data[key] !== undefined && data[key] !== "");

  return (
    <div className="border rounded p-3 bg-white mt-2">
      <div className="d-flex justify-content-between align-items-start border-bottom pb-2 mb-2">
        <div>
          <div className="fw-bold">{data.vendor_name || "(vendor name not captured)"}</div>
          <div className="text-muted small">Invoice</div>
        </div>
        <div className="text-end small">
          <div>
            Invoice #: <strong>{data.invoice_number || "—"}</strong>
          </div>
          <div>
            Date: <strong>{data.invoice_date || "—"}</strong>
          </div>
        </div>
      </div>

      {hasAddresses && (
        <div className="row small mb-3">
          <div className="col-6">
            <div className="text-muted text-uppercase" style={{ fontSize: "10px" }}>
              Bill To
            </div>
            <div style={{ whiteSpace: "pre-line" }}>{data.bill_to || "—"}</div>
          </div>
          <div className="col-6">
            <div className="text-muted text-uppercase" style={{ fontSize: "10px" }}>
              Ship To
            </div>
            <div style={{ whiteSpace: "pre-line" }}>{data.ship_to || "—"}</div>
          </div>
        </div>
      )}

      {lineItems.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-sm table-bordered mb-2">
            <thead className="table-light">
              <tr>
                {showItemNumber && <th>Item #</th>}
                <th>Description</th>
                {showUom && <th>UOM</th>}
                <th className="text-end">Qty</th>
                <th className="text-end">Unit Price</th>
                <th className="text-end">Ext. Price</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, i) => (
                <tr key={i}>
                  {showItemNumber && <td>{item.item_number}</td>}
                  <td>{item.description}</td>
                  {showUom && <td>{item.uom}</td>}
                  <td className="text-end">{item.qty}</td>
                  <td className="text-end">{item.unit_price}</td>
                  <td className="text-end">{item.extended_price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-muted small mb-2">No line items captured.</div>
      )}

      <div className="d-flex justify-content-end">
        <table className="small" style={{ minWidth: "220px" }}>
          <tbody>
            <tr>
              <td className="text-muted pe-3">Subtotal</td>
              <td className="text-end">{money(data.subtotal)}</td>
            </tr>
            {charges.map(([key, label]) => (
              <tr key={key}>
                <td className="text-muted pe-3">{label}</td>
                <td className="text-end">{money(data[key])}</td>
              </tr>
            ))}
            <tr className="border-top fw-bold">
              <td className="pe-3">Total</td>
              <td className="text-end">{money(data.total_amount)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoicePreview;
